import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { getCallNeededReminders } from "../../services/leadTrackerApi";
import { formatFlagReminder, toReminderInput } from "./LeadFlags";

// Call Needed: a customer waiting for a call from the Call Team, sometimes at a time they asked for.
// The requested time is shared - unlike a flag's personal reminder, every Call Team member is told.

/** "45 mins", "2 hrs 15 mins", "1 day 3 hrs". */
export function formatWaitDuration(milliseconds) {
  const minutes = Math.max(0, Math.floor(milliseconds / 60_000));
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return `${hours} hr${hours === 1 ? "" : "s"}${rest ? ` ${rest} min${rest === 1 ? "" : "s"}` : ""}`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return `${days} day${days === 1 ? "" : "s"}${restHours ? ` ${restHours} hr${restHours === 1 ? "" : "s"}` : ""}`;
}

function useNow(intervalMs = 60_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/**
 * Under a lead's Call Needed badge: when the customer asked to be called (and how late that call
 * is once the time passes), or, with no time asked for, how long the lead has been waiting.
 */
export function CallNeededTiming({ lead, className = "" }) {
  const now = useNow();
  if (!lead?.callNeeded) return null;
  const requested = lead.callNeededRequestedAt ? new Date(lead.callNeededRequestedAt) : null;
  const marked = lead.callNeededMarkedAt ? new Date(lead.callNeededMarkedAt) : null;
  const markedText = lead.callNeededMarkedBy ? `Marked by ${lead.callNeededMarkedBy}` : "";
  if (requested && !Number.isNaN(requested.getTime())) {
    const late = requested <= now;
    return <div className={`small mt-1 ${className}`} title={markedText}>
      <Badge bg={late ? "danger" : "info"} text={late ? undefined : "dark"}>
        <i className="bi bi-telephone-forward me-1" aria-hidden="true" />
        {late ? `Call overdue by ${formatWaitDuration(now - requested)}` : `Call at ${formatFlagReminder(lead.callNeededRequestedAt, now)}`}
      </Badge>
    </div>;
  }
  if (!marked || Number.isNaN(marked.getTime())) return null;
  return <div className={`small mt-1 ${className}`} title={markedText}>
    <Badge bg="warning" text="dark">
      <i className="bi bi-hourglass-split me-1" aria-hidden="true" />Waiting {formatWaitDuration(now - marked)}
    </Badge>
  </div>;
}

/** An interest status's follow-up time in minutes ("Details Sent: 2 hours" -> 120), or null. */
export function statusFollowUpMinutes(statusOptions, statusName) {
  const status = (statusOptions || []).find((option) => String(option?.name || "").trim().toLowerCase() === statusName.toLowerCase());
  const after = Number(status?.followUpAfter);
  if (!Number.isFinite(after) || after <= 0) return null;
  const unit = String(status?.followUpUnit || "").toUpperCase();
  if (unit === "MINUTES") return after;
  if (unit === "DAYS") return after * 1440;
  return after * 60;
}

/**
 * Under a Not Yet Called lead: how long it has waited for its first call since it was set to
 * Details Sent. It turns red once that wait passes the status's follow-up time - these leads are
 * kept off the Overdue card, so this is where a late one shows.
 */
export function NotYetCalledWaiting({ lead, targetMinutes = null, className = "" }) {
  const now = useNow();
  if (!lead?.detailsSentAt) return null;
  const since = new Date(lead.detailsSentAt);
  if (Number.isNaN(since.getTime())) return null;
  const waited = now - since;
  const late = targetMinutes != null && waited >= targetMinutes * 60_000;
  const target = targetMinutes != null ? ` Call within ${formatWaitDuration(targetMinutes * 60_000)}.` : "";
  return <div className={`small mt-1 ${className}`} title={`Waiting for the first call since Details Sent at ${since.toLocaleString("en-IN")}.${target}`}>
    <Badge bg={late ? "danger" : "warning"} text={late ? undefined : "dark"}>
      <i className="bi bi-hourglass-split me-1" aria-hidden="true" />Waiting {formatWaitDuration(waited)}
    </Badge>
  </div>;
}

function callTimePresets(now = new Date()) {
  const at = (days, hours) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, hours, 0);
  const inMinutes = (minutes) => new Date(Math.ceil((now.getTime() + minutes * 60_000) / 300_000) * 300_000);
  const presets = [["In 30 mins", inMinutes(30)], ["In 1 hour", inMinutes(60)]];
  if (now.getHours() < 18) presets.push(["Today 6 PM", at(0, 18)]);
  presets.push(["Tomorrow 10 AM", at(1, 10)], ["Tomorrow 4 PM", at(1, 16)]);
  return presets;
}

/** The time the customer asked to be called, if any. Shared by the Call Needed dialog and the lead editor. */
export function CallRequestTimeField({ value, disabled = false, onChange }) {
  const past = value && new Date(value) <= new Date();
  return <Form.Group>
    <Form.Label>Customer asked for a call at <span className="small text-muted">(optional)</span></Form.Label>
    <div className="d-flex flex-wrap gap-1 mb-2">
      {callTimePresets().map(([label, date]) => <Button key={label} size="sm" variant="outline-primary" disabled={disabled} onClick={() => onChange(toReminderInput(date))}>{label}</Button>)}
      <Button size="sm" variant="outline-secondary" disabled={disabled || !value} onClick={() => onChange("")}>Any time</Button>
    </div>
    <Form.Control type="datetime-local" value={value || ""} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    <Form.Text className={past ? "text-danger" : "text-muted"}>
      {past ? "This time has already passed - the Call Team will be reminded straight away."
        : value ? "Everyone logged in to the Call Team portal gets a reminder at this time."
          : "With no time, the lead shows how long it has been waiting."}
    </Form.Text>
  </Form.Group>;
}

/** Marking a lead Call Needed from the lead list: the reason, and the customer's time if they named one. */
export function CallNeededModal({ lead, saving, onSave, onHide }) {
  const [note, setNote] = useState("");
  const [requestedAt, setRequestedAt] = useState("");
  useEffect(() => {
    setNote(lead?.callNeededNote || "");
    setRequestedAt(toReminderInput(lead?.callNeededRequestedAt));
  }, [lead?.id, lead?.callNeededNote, lead?.callNeededRequestedAt]);
  if (!lead) return null;
  return <Modal show onHide={saving ? undefined : onHide} centered>
    <Modal.Header closeButton={!saving}>
      <Modal.Title><i className="bi bi-telephone-inbound text-danger me-2" aria-hidden="true" />Call Needed · {lead.leadId}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form.Group className="mb-3">
        <Form.Label>Reason</Form.Label>
        <Form.Control as="textarea" rows={2} value={note} disabled={saving} placeholder="e.g. Wants to discuss the hotel options" onChange={(event) => setNote(event.target.value)} />
      </Form.Group>
      <CallRequestTimeField value={requestedAt} disabled={saving} onChange={setRequestedAt} />
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" disabled={saving} onClick={onHide}>Cancel</Button>
      <Button variant="danger" disabled={saving} onClick={() => onSave(note, requestedAt)}>
        {saving ? <><Spinner size="sm" className="me-2" />Saving</> : "Mark Call Needed"}
      </Button>
    </Modal.Footer>
  </Modal>;
}

// ---- Reminder pop-ups --------------------------------------------------------------------------

const shownKey = (memberId) => `callNeededRemindersShown:${memberId}`;
const snoozeKey = (memberId) => `callNeededRemindersSnoozed:${memberId}`;
const readJson = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; }
};
const writeJson = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
};

/**
 * Pops a reminder for every Call Team member logged in once a customer's requested call time has
 * passed, and once on login for any that passed while away. Snoozing is personal: it only delays
 * this member's pop-up and leaves the customer's time alone.
 */
export function useCallNeededReminderPopups({ enabled, token, memberId, onOpen }) {
  const [reminders, setReminders] = useState([]);
  const handlers = useRef({ onOpen, token });
  handlers.current = { onOpen, token };

  const refresh = useCallback(async () => {
    if (!enabled || !memberId) return;
    try {
      setReminders(await getCallNeededReminders(handlers.current.token()));
    } catch {
      // The next poll retries.
    }
  }, [enabled, memberId]);

  useEffect(() => {
    if (!enabled || !memberId) return undefined;
    void refresh();
    const poller = window.setInterval(() => { void refresh(); }, 60_000);
    return () => window.clearInterval(poller);
  }, [enabled, memberId, refresh]);

  useEffect(() => {
    if (!enabled || !memberId) return undefined;
    const check = () => {
      const shown = new Set(readJson(shownKey(memberId), []));
      const snoozed = readJson(snoozeKey(memberId), {});
      const now = new Date();
      let changed = false;
      reminders.forEach((reminder) => {
        if (!reminder.requestedAt || new Date(reminder.requestedAt) > now) return;
        // A changed time is a new reminder.
        const key = `${reminder.leadId}|${reminder.requestedAt}`;
        if (snoozed[key] && new Date(snoozed[key]) > now) return;
        if (shown.has(key) && !snoozed[key]) return;
        shown.add(key);
        delete snoozed[key];
        changed = true;
        const snooze = (minutes) => {
          const current = readJson(snoozeKey(memberId), {});
          current[key] = new Date(Date.now() + minutes * 60_000).toISOString();
          writeJson(snoozeKey(memberId), current);
        };
        toast.error(({ closeToast }) => <div>
          <div className="fw-semibold"><i className="bi bi-telephone-forward me-1" aria-hidden="true" />Call now: {reminder.customerName || reminder.customerNumber || reminder.leadCode}</div>
          <div className="small">{reminder.leadCode}{reminder.destinationName ? ` · ${reminder.destinationName}` : ""} · asked for {formatFlagReminder(reminder.requestedAt)}</div>
          {reminder.customerNumber && <div className="small">{reminder.customerNumber}</div>}
          {reminder.note && <div className="small fst-italic">{reminder.note}</div>}
          <div className="d-flex flex-wrap gap-1 mt-2">
            <Button size="sm" variant="light" onClick={() => { handlers.current.onOpen?.(reminder); closeToast?.(); }}>Show</Button>
            <Button size="sm" variant="outline-light" onClick={() => { snooze(10); closeToast?.(); }}>Snooze 10 min</Button>
            <Button size="sm" variant="outline-light" onClick={() => { snooze(30); closeToast?.(); }}>30 min</Button>
          </div>
        </div>, { autoClose: false, closeOnClick: false, toastId: `call-needed-${key}` });
      });
      if (changed) {
        writeJson(shownKey(memberId), [...shown].slice(-300));
        writeJson(snoozeKey(memberId), snoozed);
      }
    };
    check();
    const timer = window.setInterval(check, 30_000);
    return () => window.clearInterval(timer);
  }, [enabled, memberId, reminders]);

  return { reminders, refresh };
}
